import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import {
  Camera, CheckCircle, Clock, Desktop, MagnifyingGlass, MapPin, Plus, Spinner,
  Stop, Trash, Users, WarningCircle, WifiHigh,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

function clock(value) {
  if (value == null) return "—";
  const seconds = Math.max(0, Number(value) || 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return hours
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`
    : `${minutes}:${String(rest).padStart(2, "0")}`;
}

function statusLabel(status, paused = false) {
  if (paused) return "Em pausa";
  if (status === "in_progress") return "Em prova";
  if (status === "finished") return "Finalizada";
  return "Aguardando início";
}

function Metric({ label, value, detail, tone = "default", icon: Icon }) {
  const tones = {
    default: "border-border bg-card",
    primary: "border-primary bg-primary/5",
    green: "border-emerald-600 bg-emerald-50",
    amber: "border-amber-500 bg-amber-50",
  };
  return <div className={`border p-5 ${tones[tone] || tones.default}`}>
    <div className="flex items-center justify-between gap-3">
      <div className="label-eyebrow">{label}</div>
      {Icon && <Icon size={22} className={tone === "primary" ? "text-primary" : "text-muted-foreground"} />}
    </div>
    <div className="font-display text-4xl font-black mt-2">{value}</div>
    {detail && <div className="text-xs text-muted-foreground mt-2">{detail}</div>}
  </div>;
}

function EvidenceImage({ sessionId, event, kind = "camera", autoLoad = false }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState("");
  const autoLoadAttempted = useRef(false);

  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
  }, [url]);

  const available = kind === "screen" ? event.screen_snapshot_available : event.snapshot_available;
  const status = kind === "screen" ? event.screen_snapshot_status : event.snapshot_status;
  const statusError = kind === "screen" ? event.screen_snapshot_error : event.snapshot_error;
  const label = kind === "screen" ? "captura da tela" : "foto da câmera";
  const EvidenceIcon = kind === "screen" ? Desktop : Camera;

  const loadEvidence = useCallback(async (openExpanded = true) => {
    if (url) {
      if (openExpanded) setExpanded(true);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const suffix = kind === "screen" ? "screen-snapshot" : "snapshot";
      const response = await api.get(`/live-sessions/${sessionId}/proctoring/${event.id}/${suffix}`, {
        responseType: "blob",
      });
      const objectUrl = URL.createObjectURL(response.data);
      setUrl(objectUrl);
      setExpanded(openExpanded);
    } catch (requestError) {
      setError(fmtError(requestError));
    } finally {
      setLoading(false);
    }
  }, [event.id, kind, sessionId, url]);

  useEffect(() => {
    if (autoLoad && available && !url && !loading && !autoLoadAttempted.current) {
      autoLoadAttempted.current = true;
      void loadEvidence(false);
    }
  }, [autoLoad, available, loadEvidence, loading, url]);

  if (!available) {
    const messages = {
      capture_unavailable: `Registro confirmado, mas não foi possível obter a ${label} naquele instante.`,
      missing: `Registro confirmado sem ${label}.`,
      rejected: statusError || `A ${label} chegou, mas foi rejeitada na validação.`,
    };
    const message = messages[status];
    return message ? <div className="mt-3 border border-border bg-white p-3 text-xs text-muted-foreground"><EvidenceIcon className="inline mr-2" />{message}</div> : null;
  }

  return <>
    <Button type="button" size="sm" variant="outline" onClick={() => loadEvidence(true)} disabled={loading} className="rounded-none mt-3">
      {loading ? <Spinner className="animate-spin mr-2" /> : <EvidenceIcon className="mr-2" />}{url ? `Ampliar ${label}` : `Carregar ${label}`}
    </Button>
    {error && <div className="text-xs text-destructive mt-2">Não foi possível abrir a evidência: {error}</div>}
    {url && <button type="button" onClick={() => setExpanded(true)} className="block mt-3 border border-border bg-black max-w-60"><img src={url} alt={`${label} de ${event.display_name}`} className="max-h-40 w-full object-contain" /></button>}
    {expanded && url && <div className="fixed inset-0 z-[120] bg-black/90 grid place-items-center p-4" role="dialog" aria-modal="true" aria-label={`Evidência visual: ${label}`}>
      <button type="button" onClick={() => setExpanded(false)} className="absolute right-5 top-5 h-11 w-11 border border-white text-white text-2xl" aria-label="Fechar evidência">×</button>
      <div className="max-w-5xl max-h-[90vh] text-center"><img src={url} alt={`${label} ampliada de ${event.display_name}`} className="max-h-[80vh] max-w-full object-contain mx-auto" /><div className="text-white text-sm mt-3">{event.display_name} · {event.label} · {event.received_at ? new Date(event.received_at).toLocaleString("pt-BR") : ""}</div></div>
    </div>}
  </>;
}

export default function OnlineExamDashboard({ session, acting, onSessionChange, onFinish }) {
  const [monitor, setMonitor] = useState({ summary: {}, participants: [], progress_distribution: [], completion_timeline: [], question_performance: [] });
  const [proctoring, setProctoring] = useState({ events: [], identity_checkpoints: [], pending_count: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [reviewing, setReviewing] = useState("");
  const [extraMinutes, setExtraMinutes] = useState("10");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const polling = useRef(false);

  const load = useCallback(async (quiet = true) => {
    if (!session?.id || polling.current) return;
    polling.current = true;
    try {
      const requests = [api.get(`/live-sessions/${session.id}/monitor`)];
      if (session.proctoring_enabled || session.single_screen_required || session.location_required) requests.push(api.get(`/live-sessions/${session.id}/proctoring`));
      const [monitorResponse, proctorResponse] = await Promise.all(requests);
      setMonitor(monitorResponse.data || {});
      if (proctorResponse) setProctoring(proctorResponse.data || {});
    } catch (error) {
      if (!quiet) toast.error(fmtError(error));
    } finally {
      polling.current = false;
      setLoading(false);
    }
  }, [session?.id, session?.location_required, session?.proctoring_enabled, session?.single_screen_required]);

  useEffect(() => {
    load(false);
    if (session?.phase === "finished") return undefined;
    const timer = window.setInterval(load, 2500);
    return () => window.clearInterval(timer);
  }, [load, session?.phase]);

  const addTime = async (participantId = null, fixedMinutes = null) => {
    const minutes = Number(fixedMinutes || extraMinutes);
    if (!Number.isFinite(minutes) || minutes < 1 || minutes > 180) {
      toast.error("Informe de 1 a 180 minutos.");
      return;
    }
    const scope = participantId ? "participant" : "all";
    if (scope === "all" && !window.confirm(`Adicionar ${minutes} minutos a todas as tentativas, inclusive às que ainda não começaram?`)) return;
    setBusy(participantId || "all");
    try {
      const response = await api.post(`/live-sessions/${session.id}/extra-time`, {
        scope, participant_id: participantId, extra_minutes: minutes,
      });
      if (response.data.session) onSessionChange(response.data.session);
      toast.success(scope === "all" ? `${minutes} minutos adicionados para todos.` : `${minutes} minutos adicionados para o aluno.`);
      await load();
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setBusy("");
    }
  };

  const review = async (eventId, status) => {
    setReviewing(eventId);
    try {
      await api.post(`/live-sessions/${session.id}/proctoring/${eventId}/review`, { status });
      await load();
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setReviewing("");
    }
  };

  const deleteCorrection = async (participant) => {
    const confirmed = window.confirm(
      `Excluir somente a correção online de ${participant.display_name}?\n\nA prova, as respostas da tentativa e as evidências de fiscalização serão preservadas. Essa correção deixará de aparecer nos resultados e relatórios.`,
    );
    if (!confirmed) return;
    setBusy(`delete:${participant.id}`);
    try {
      await api.delete(`/live-sessions/${session.id}/corrections/${participant.id}`);
      toast.success(`Correção de ${participant.display_name} excluída. A tentativa foi preservada.`);
      await load();
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setBusy("");
    }
  };

  const summary = monitor.summary || {};
  const filtered = useMemo(() => (monitor.participants || []).filter((item) => {
    const matchesQuery = !query || String(item.display_name || "").toLocaleLowerCase("pt-BR").includes(query.toLocaleLowerCase("pt-BR"));
    const matchesStatus = statusFilter === "all"
      || (statusFilter === "online" ? item.online
        : statusFilter === "paused" ? item.voluntary_pause_active
          : item.attempt_status === statusFilter);
    return matchesQuery && matchesStatus;
  }), [monitor.participants, query, statusFilter]);
  const timeline = (monitor.completion_timeline || []).map((item) => ({
    ...item,
    label: new Date(item.at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
  }));
  const difficultQuestions = [...(monitor.question_performance || [])]
    .filter((item) => item.answered > 0)
    .sort((a, b) => a.accuracy_percent - b.accuracy_percent)
    .slice(0, 12);

  return <section className="space-y-5" data-testid="online-proctoring-dashboard" data-dashboard="online-exam-dashboard">
    <div className="flex flex-wrap items-center justify-between gap-4 border border-border bg-card p-5">
      <div>
        <div className="label-eyebrow">central de acompanhamento</div>
        <h2 className="font-display text-3xl font-black mt-1">{session.exam_name}</h2>
        <p className="text-sm text-muted-foreground mt-2">Atualização automática · dados de atividade validados pelo servidor</p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        {session.phase !== "finished" && <>
          <label>
            <span className="label-eyebrow">tempo extra para todos</span>
            <Input type="number" min="1" max="180" value={extraMinutes} onChange={(event) => setExtraMinutes(event.target.value)} className="rounded-none mt-1 w-28" />
          </label>
          <Button variant="outline" disabled={Boolean(busy)} onClick={() => addTime()} className="rounded-none">
            {busy === "all" ? <Spinner className="animate-spin mr-2" /> : <Plus className="mr-2" />}Adicionar minutos
          </Button>
          <Button disabled={acting} onClick={() => window.confirm("Encerrar a prova agora finalizará todas as tentativas em andamento. Deseja continuar?") && onFinish()} className="rounded-none bg-destructive hover:bg-destructive/90">
            <Stop weight="fill" className="mr-2" />Encerrar prova
          </Button>
        </>}
      </div>
    </div>

    <div className="grid sm:grid-cols-2 xl:grid-cols-6 gap-3">
      <Metric label="alunos online" value={summary.online || 0} detail={`${summary.joined || 0} já acessaram`} tone="primary" icon={WifiHigh} />
      <Metric label="ainda não entraram" value={summary.not_joined || 0} detail={`${summary.expected_students || 0} alunos previstos`} icon={Users} />
      <Metric label="respondendo" value={Math.max(0, Number(summary.in_progress || 0) - Number(summary.paused || 0))} detail={`${summary.not_started || 0} aguardando início`} tone="amber" icon={Clock} />
      <Metric label="em pausa" value={summary.paused || 0} detail="tempo total continua correndo" icon={Clock} />
      <Metric label="finalizaram" value={summary.finished || 0} detail={`${summary.completion_percent || 0}% da turma prevista`} tone="green" icon={CheckCircle} />
      <Metric label="média final" value={`${summary.average_score_percent || 0}%`} detail="entre provas finalizadas" icon={CheckCircle} />
    </div>

    <div className="grid xl:grid-cols-2 gap-5">
      <div className="border border-border bg-card p-5 min-h-[330px]">
        <div className="label-eyebrow">quantos alunos concluíram quantas questões</div>
        <div className="text-sm text-muted-foreground mt-2">Distribuição do progresso individual</div>
        <div className="h-64 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monitor.progress_distribution || []} margin={{ top: 8, right: 8, left: -20, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="questions_completed" tick={{ fontSize: 11 }} label={{ value: "questões concluídas", position: "insideBottom", offset: -2, fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(value) => [value, "alunos"]} labelFormatter={(value) => `${value} questões`} />
              <Bar dataKey="students" fill="hsl(var(--primary))" radius={0} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="border border-border bg-card p-5 min-h-[330px]">
        <div className="label-eyebrow">evolução da conclusão</div>
        <div className="text-sm text-muted-foreground mt-2">Inícios e finalizações acumulados</div>
        <div className="h-64 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={timeline} margin={{ top: 8, right: 8, left: -20, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Area type="monotone" dataKey="started" name="Iniciaram" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.16)" strokeWidth={2} />
              <Area type="monotone" dataKey="finished" name="Finalizaram" stroke="#059669" fill="#10b9812a" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>

    <div className="border border-border bg-card">
      <div className="border-b border-border p-5 flex flex-wrap items-end justify-between gap-3">
        <div><div className="label-eyebrow">acompanhamento por aluno</div><div className="text-sm text-muted-foreground mt-2">{filtered.length} de {(monitor.participants || []).length} participantes</div></div>
        <div className="flex flex-wrap gap-2">
          <label className="relative"><MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar aluno" className="rounded-none pl-10 w-56" /></label>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="h-10 border border-input bg-background px-3 text-sm rounded-none">
            <option value="all">Todos</option><option value="online">Online agora</option><option value="not_started">Aguardando</option><option value="in_progress">Em prova</option><option value="paused">Em pausa</option><option value="finished">Finalizados</option>
          </select>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[650px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card border-b border-border text-left"><tr>
            <th className="p-3">Aluno</th><th className="p-3">Situação</th><th className="p-3 min-w-52">Progresso</th><th className="p-3">Acertos</th><th className="p-3">Tempo</th><th className="p-3">Ações</th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan="6" className="p-10 text-center"><Spinner className="animate-spin mx-auto" /></td></tr>}
            {!loading && !filtered.length && <tr><td colSpan="6" className="p-10 text-center text-muted-foreground">Nenhum aluno neste filtro.</td></tr>}
            {filtered.map((item) => <tr key={item.id} className={item.online ? "bg-primary/[0.025]" : ""}>
              <td className="p-3"><div className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${item.online ? "bg-emerald-500" : "bg-muted-foreground/30"}`} /><b>{item.display_name}</b></div>{item.extra_time_minutes > 0 && <div className="text-xs text-primary mt-1">+{item.extra_time_minutes} min individual</div>}</td>
              <td className="p-3"><span className="font-medium">{statusLabel(item.attempt_status, item.voluntary_pause_active)}</span>{item.voluntary_pause_active && <div className="text-xs text-primary mt-1">pausa atual: {clock(item.voluntary_pause_elapsed_seconds)}</div>}{item.online && <div className="text-xs text-emerald-700 mt-1">online agora</div>}</td>
              <td className="p-3"><div className="flex justify-between text-xs"><span>{item.questions_completed}/{item.total_questions}</span><span>{item.completion_percent}%</span></div><div className="h-2 bg-muted mt-2"><div className={`h-full ${item.attempt_status === "finished" ? "bg-emerald-600" : "bg-primary"}`} style={{ width: `${item.completion_percent}%` }} /></div></td>
              <td className="p-3"><b>{item.accuracy_percent}%</b><div className="text-xs text-muted-foreground mt-1">{item.hits}/{item.answered} respondidas certas · nota {item.score_percent}%</div></td>
              <td className="p-3 font-mono-data"><b>{clock(item.total_remaining_seconds)}</b>{item.voluntary_pause_count > 0 && <div className="text-xs text-muted-foreground mt-1">{item.voluntary_pause_count} pausa(s) · {clock(item.voluntary_pause_total_seconds)}</div>}</td>
              <td className="p-3"><div className="flex flex-wrap gap-1">
                {item.attempt_status !== "finished" && <><Button size="sm" variant="outline" disabled={Boolean(busy)} onClick={() => addTime(item.id, 5)} className="rounded-none">+5 min</Button><Button size="sm" variant="outline" disabled={Boolean(busy)} onClick={() => addTime(item.id, 10)} className="rounded-none">+10 min</Button></>}
                {item.join_location?.latitude != null && <a title="Ver local registrado" className="inline-flex h-9 items-center border border-border px-2 text-primary" href={`https://www.google.com/maps?q=${item.join_location.latitude},${item.join_location.longitude}`} target="_blank" rel="noreferrer noopener"><MapPin weight="fill" /></a>}
                {item.correction_available && <Button size="sm" variant="outline" disabled={Boolean(busy)} onClick={() => deleteCorrection(item)} className="rounded-none border-destructive text-destructive hover:bg-destructive/10" title="Excluir apenas esta correção online"><Trash className="mr-1" />Excluir correção</Button>}
                {item.correction_deleted && !item.correction_available && <span className="inline-flex h-9 items-center px-2 text-xs text-muted-foreground">Correção excluída</span>}
              </div></td>
            </tr>)}
          </tbody>
        </table>
      </div>
    </div>

    {session.proctoring_enabled && <div className="border border-border bg-card" data-testid="identity-checkpoint-gallery">
      <div className="border-b border-border p-5 flex flex-wrap items-end justify-between gap-3">
        <div><div className="label-eyebrow">fotos de identidade</div><div className="text-sm text-muted-foreground mt-2">Fotos recebidas no início e em cada questão. São checkpoints de identidade, não ocorrências.</div></div>
        <div className="font-display text-3xl font-black">{proctoring.identity_checkpoint_total || 0}</div>
      </div>
      <div className="max-h-[430px] overflow-y-auto grid md:grid-cols-2 xl:grid-cols-3 gap-px bg-border">
        {!(proctoring.identity_checkpoints || []).length && <div className="md:col-span-2 xl:col-span-3 bg-card p-8 text-center text-muted-foreground"><Camera size={34} className="mx-auto mb-3" />As fotos aparecerão quando os alunos iniciarem a prova.</div>}
        {(proctoring.identity_checkpoints || []).slice(0, 300).map((item) => <div key={item.id} className="bg-card p-4">
          <div className="flex items-start gap-3"><Camera size={24} weight="fill" className="text-emerald-700 shrink-0" /><div><b>{item.display_name}</b><div className="text-sm mt-1">{item.event_type === "identity_start" ? "Início da prova" : `Questão ${item.question_position || "—"}`}</div><div className="text-xs text-muted-foreground mt-1">{item.received_at ? new Date(item.received_at).toLocaleString("pt-BR") : ""}</div></div></div>
          <EvidenceImage sessionId={session.id} event={item} />
        </div>)}
      </div>
    </div>}

    <div className="grid xl:grid-cols-[minmax(0,0.65fr)_minmax(420px,1fr)] gap-5">
      <div className="border border-border bg-card p-5">
        <div className="label-eyebrow">questões com menor acerto</div>
        <p className="text-sm text-muted-foreground mt-2">Considera somente respostas já enviadas.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
          {difficultQuestions.map((item) => <div key={item.position} className="border border-border p-3"><div className="text-xs text-muted-foreground">Questão {item.position}</div><b className="text-xl">{item.accuracy_percent}%</b><div className="text-xs mt-1">{item.hits}/{item.answered}</div></div>)}
          {!difficultQuestions.length && <div className="text-sm text-muted-foreground col-span-full py-6">As taxas aparecerão após as primeiras respostas.</div>}
        </div>
      </div>

      <div className="border border-border bg-card">
        <div className="border-b border-border p-5 flex justify-between gap-3"><div><div className="label-eyebrow">ocorrências para revisão</div><div className="text-sm text-muted-foreground mt-2">O sistema não acusa, não elimina e não altera nota automaticamente. Os sinais exigem revisão humana.</div></div><div className="font-display text-3xl font-black">{proctoring.pending_count || 0}</div></div>
        <div className="max-h-[430px] overflow-y-auto divide-y divide-border">
          {!session.proctoring_enabled && !session.single_screen_required && !session.location_required && <div className="p-8 text-center text-muted-foreground"><Camera size={34} className="mx-auto mb-3" />Fiscalização não ativada nesta aplicação.</div>}
          {(session.proctoring_enabled || session.single_screen_required || session.location_required) && !(proctoring.events || []).length && <div className="p-8 text-center text-muted-foreground"><CheckCircle size={34} className="mx-auto mb-3 text-emerald-600" />Nenhuma ocorrência registrada.</div>}
          {(proctoring.events || []).map((item, index) => <div key={item.id} className={`p-4 ${item.review_status === "pending" ? "bg-amber-50/60" : ""}`}>
            <div className="flex gap-3"><WarningCircle size={24} weight="fill" className={item.severity === "red" ? "text-destructive" : item.severity === "orange" ? "text-orange-600" : "text-amber-600"} /><div className="min-w-0 flex-1"><b>{item.display_name}</b><div className="text-sm mt-1">{item.label}</div><div className="text-xs text-muted-foreground mt-1">{item.received_at ? new Date(item.received_at).toLocaleString("pt-BR") : ""} · {item.review_status || "pending"}</div></div></div>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {session.proctoring_enabled && <div className="border border-border bg-white p-3"><div className="label-eyebrow">câmera do aluno</div><EvidenceImage sessionId={session.id} event={item} /></div>}
              {(item.screen_snapshot_available || (item.screen_snapshot_status && item.screen_snapshot_status !== "not_requested")) && <div className="border-2 border-primary bg-primary/5 p-3" data-testid="screen-occurrence-evidence"><div className="label-eyebrow text-primary">tela no momento da ocorrência</div><EvidenceImage sessionId={session.id} event={item} kind="screen" autoLoad={index < 24} /></div>}
            </div>
            <div className="flex flex-wrap gap-2 mt-3"><Button size="sm" variant="outline" disabled={reviewing === item.id} onClick={() => review(item.id, "disregarded")} className="rounded-none">Desconsiderar</Button><Button size="sm" variant="outline" disabled={reviewing === item.id} onClick={() => review(item.id, "justified")} className="rounded-none">Justificada</Button><Button size="sm" disabled={reviewing === item.id} onClick={() => review(item.id, "confirmed")} className="rounded-none">Confirmar</Button></div>
          </div>)}
        </div>
      </div>
    </div>
  </section>;
}
